import React, { useMemo } from "react";
import * as io from "io-ts";
import WidgetCard from "@ui/WidgetCard";
import t from "../../../../../i18n";
import Switch from "@ui/Switch";
import { styled } from "@linaria/react";
import { css } from "@linaria/core";
import Statistic from "@ui/Statistic";
import createGlobalState from "@ui/lib/createGlobalState";
import type { ActiveAnalysis } from "../../../../../api/useActiveAnalysesStream";
import computeInternalGap from "../../../../../domain/computeInternalGap";
import type { Setup } from "../../../../../api/useSetups";
import formatNumber from "@ui/i18n/formatNumber";
import type { ColumnMetaData } from "@ui/MiniTable";
import MiniTable from "@ui/MiniTable";
import Text from "@ui/Text";
import { withKeyNotNull } from "diagnostics-lib/typescript";
import { comparatorBy } from "diagnostics-lib/comparator";
import { indexByKey } from "diagnostics-lib/iterable";
import Table from "@ui/Table";
import { color, fonts, lineHeights } from "@ui";
import useSortedData from "../../../../../lib/hooks/useSortedData";
import type { ColumnMetaData as ColumnMetaDataTable } from "@ui/Table/ColumnMetaData";
import createIOSortType from "../../../../../lib/createIOSortType";
import getQuantityOffset from "../../../../../api/getQuantityOffset";
import type { Project } from "../../../../../api/useProjects";
import LoadingBoundary from "@ui/LoadingBoundary";
import { useTranslations } from "../../../../../api/useTranslations";
import { type Quantity } from "../../../../../api/useQuantities";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import { isAnalysisQuantityValid } from "../../../metrology";
import { useExternalGaps } from "../../../../../api/useExternalGaps";

type ExternalGapTableColumns = "order" | "boundaryCondition";
export const columns: ExternalGapTableColumns[] = ["order", "boundaryCondition"];
const useSort = createGlobalState({
  storage: "session",
  key: "externalGap-sort",
  type: createIOSortType(columns),
  defaultValue: {
    column: "order",
    direction: "asc",
  },
  scoped: true,
});

const styledWrapperCard = css`
  position: relative;
`;
const Position = styled.div`
  position: absolute;
  right: 24px;
`;

const StyledTable = styled.div`
  position: relative;

  table thead {
    position: sticky;
    top: 0;
    z-index: 1;
    tr {
      th {
        span {
          color: var(--carbon800);
          font: ${fonts.body2};
        }
        background-color: ${color.titanium100} !important;
        min-height: 32px !important;
      }
    }
  }

  table tbody {
    font: ${fonts.body1};
    tr {
      min-height: fit-content;
      td {
        min-height: fit-content;
        padding: 12px;
        font: ${fonts.body1};
        line-height: ${lineHeights[1]};
        color: ${color.carbon900};
      }
    }
  }
`;
const SectionLoss = styled.div`
  width: 100%;
  & > *:not(:first-child) {
    margin-top: 10px;
  }
`;

const breakpoints = {
  medium: "min-width: 1280px",
} as const;
const ScrollableTable = styled.div`
  max-height: none;

  @media (${breakpoints.medium}) {
    max-height: 212px;
    overflow-y: auto;
  }

  table thead {
    position: sticky;
    top: 0;
  }
`;

const statisticsStyled = css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;
const StatisticsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  grid-gap: 24px;
`;

export const useDetailsWidgetLosses = createGlobalState({
  storage: "session",
  key: "switchlossWidget",
  type: io.boolean,
  defaultValue: false,
  scoped: true,
});

// Internal losses
interface InternalLosses {
  name: string;
  measured: number | null;
  expected: number | null;
  losses: number | null;
  unit: string | null;
}
const internalColumn = ["name", "measured", "expected", "losses"] as const;
type InternalColumn = (typeof internalColumn)[number];

// External losses
const externalColumn = ["order", "boundaryCondition", "measured", "reference"] as const;
type ExternalColumn = (typeof externalColumn)[number];
interface ExternalLosses {
  order: number | null;
  boundaryCondition: string | null;
  measured: number | null;
  reference: number | null;
  unit: string | null;
  losses: { [key: string]: number | undefined };
}

const externalInputMetadata: { [key in ExternalColumn]: ColumnMetaDataTable<ExternalLosses> } = {
  order: {
    label: t("summary.widget.losses.external_loss.metadata.order"),
    render: (e: ExternalLosses) => e.order,
    showEmptyMark: true,
    sortable: true,
    minWidth: "90px",
    maxWidth: "90px",
  },
  boundaryCondition: {
    sticky: true,
    label: t("summary.widget.losses.external_loss.metadata.boundary_condition"),
    render: (e: ExternalLosses) => e.boundaryCondition ?? undefined,
    minWidth: "150px",
    // maxWidth: "250px",
    alignment: "left",
    showEmptyMark: true,
    sortable: true,
  },
  measured: {
    label: t("summary.widget.losses.external_loss.metadata.measured"),
    render: (e: ExternalLosses) =>
      e.measured === null
        ? undefined
        : formatNumber(e.measured, { precision: 1, unit: e.unit ?? undefined }),
    minWidth: "100px",
    maxWidth: "100px",
    alignment: "left",
    showEmptyMark: true,
  },
  reference: {
    label: t("summary.widget.losses.external_loss.metadata.reference"),
    render: (e: ExternalLosses) =>
      e.reference === null
        ? undefined
        : formatNumber(e.reference, { precision: 1, unit: e.unit ?? undefined }),
    minWidth: "100px",
    maxWidth: "100px",
    alignment: "left",
    showEmptyMark: true,
  },
};

interface P {
  project: Project;
  setups: Setup[];
  activeAnalysis: ActiveAnalysis;
  quantitiesOfInterest: Quantity[];
  quantities: Quantity[];
}

export function Losses({ project, activeAnalysis, quantities, quantitiesOfInterest, setups }: P) {
  const translations = useTranslations();
  const [stateSwitch, setStateSwitch] = useDetailsWidgetLosses({
    scope: project.id,
  });
  const indexQuantity = useMemo(() => indexByKey(quantities, "id"), [quantities]);
  const setup = getAnalysisSetup(setups, activeAnalysis);
  const externalGaps = useExternalGaps(project.id, activeAnalysis.id);

  const inputMetadata: { [key in InternalColumn]: ColumnMetaData<InternalLosses> } = useMemo(
    () => ({
      name: {
        label: t("summary.widget.losses.performanceIndicator"),
        render: (l: InternalLosses) => l.name,
        minWidth: "150px",
        showEmptyMark: true,
      },
      measured: {
        label: t("summary.widget.losses.measured"),
        render: (l: InternalLosses) =>
          l?.measured === null || l?.measured === undefined
            ? undefined
            : formatNumber(l?.measured, { precision: 1, unit: l.unit ?? undefined }),
        minWidth: "90px",
        alignment: "left",
        showEmptyMark: true,
      },
      expected: {
        label: t("summary.widget.losses.expected"),
        render: (l: InternalLosses) =>
          l?.expected === null || l?.expected === undefined
            ? undefined
            : formatNumber(l?.expected, { precision: 1, unit: l.unit ?? undefined }),
        minWidth: "90px",
        alignment: "left",
        showEmptyMark: true,
      },
      losses: {
        label: t("summary.widget.losses.internal", translations),
        render: (l: InternalLosses) =>
          l?.losses === null || l?.losses === undefined
            ? undefined
            : formatNumber(l?.losses, { precision: 1, unit: l.unit ?? undefined, sign: true }),
        minWidth: "90px",
        alignment: "left",
        showEmptyMark: true,
      },
    }),
    [translations],
  );

  const internalLossesInputs = React.useMemo(
    () =>
      quantitiesOfInterest.map(qoi => {
        const quantityValue = activeAnalysis.quantityValues[qoi.id];
        if (!quantityValue)
          return {
            name: qoi.name,
            measured: null,
            expected: null,
            losses: null,
            unit: qoi.unit,
          };

        const valid = isAnalysisQuantityValid(activeAnalysis, qoi);
        const offset = getQuantityOffset(setup, qoi.id);

        const losses = valid ? computeInternalGap(activeAnalysis, qoi.id, offset) : null;

        const measured = valid ? quantityValue.measured : null;
        const simulated = valid ? quantityValue.simulated : null;
        const expected = simulated === null ? null : simulated + offset;

        return {
          name: qoi.name,
          measured,
          expected,
          losses,
          unit: qoi.unit,
        };
      }),
    [quantitiesOfInterest, activeAnalysis, setup],
  );

  const dynamicMetadata = React.useMemo(
    () =>
      quantitiesOfInterest.reduce<{
        [key: string]: ColumnMetaData<ExternalLosses>;
      }>((acc, qoi) => {
        const name = qoi.name;
        acc[name] = {
          minWidth: "150px",
          //   maxWidth: "150px",
          showEmptyMark: true,
          label: t("summary.failures.table.loss", name),
          render(externalGap) {
            const loss = externalGap.losses[name];
            /**
             *We reverse the signs for a question of wording because we're talking about impact.
             */
            return loss === undefined
              ? undefined
              : `${formatNumber(Number(loss * -1), {
                  unit: qoi.unit ?? undefined,
                  precision: 1,
                  sign: true,
                })}`;
          },
        };
        return acc;
      }, {}),
    [quantitiesOfInterest],
  );

  const externalGapInputs = React.useMemo(
    () =>
      externalGaps // no result from server (cac/calculations in progress or no external gaps)
        ? (setup?.simulationInputs
            .filter(withKeyNotNull("orderExternalGap"))
            .sort(comparatorBy("orderExternalGap"))
            ?.map(simulationInput => {
              const measured = activeAnalysis.quantityValues[simulationInput.quantityId]?.measured;
              const quantity = indexQuantity.getOrThrow(simulationInput.quantityId);
              const losses = quantitiesOfInterest.reduce<{ [key: string]: number | undefined }>(
                (acc, qoi) => {
                  const output = setup?.simulationOutputs.find(f => f.quantityId === qoi.id);
                  if (!output) return acc;
                  const value = externalGaps[simulationInput.quantityId]?.[output.quantityId];
                  acc[qoi.name] = value;
                  return acc;
                },
                {},
              );

              return {
                order: simulationInput.orderExternalGap ?? null,
                boundaryCondition: quantity.name,
                measured:
                  measured === undefined
                    ? null
                    : measured + getQuantityOffset(setup, simulationInput.quantityId),
                reference: simulationInput.referenceValue ?? null,
                unit: quantity?.unit ?? null,
                losses,
              };
            }) ?? [])
        : [],
    [activeAnalysis.quantityValues, externalGaps, indexQuantity, quantitiesOfInterest, setup],
  );

  const [sort, setSort] = useSort({ scope: project.id });
  const sortedExternalLossesInputs = useSortedData(
    externalGapInputs,
    s => s[sort.column],
    sort.direction,
  );

  const handleChange = React.useCallback(() => {
    setStateSwitch(toggle => !toggle);
  }, [setStateSwitch]);

  return (
    <WidgetCard
      title={t("commom.widgets.losses.title", translations)}
      data-cy="losses-section"
      className={styledWrapperCard}
      actions={
        <Position>
          <Switch position="right" checked={stateSwitch} onChange={handleChange}>
            {t("summaryPage.widgets.losses.switch.label")}
          </Switch>
        </Position>
      }
    >
      <StatisticsWrapper>
        {internalLossesInputs.map(m => (
          <Statistic
            className={statisticsStyled}
            key={m.name}
            title={m.name}
            value={m.losses}
            suffix={m.unit ?? undefined}
            precision={2}
            sign
          />
        ))}
      </StatisticsWrapper>
      {stateSwitch && (
        <SectionLoss>
          <Text type="h4">
            {t("summaryPage.widgets.losses.title.internal_losses", translations)}
          </Text>
          <ScrollableTable>
            <MiniTable
              data={internalLossesInputs}
              metadata={inputMetadata}
              computeRowKey={(losses: InternalLosses) => losses.name}
            />
          </ScrollableTable>
        </SectionLoss>
      )}
      <LoadingBoundary>
        {stateSwitch && sortedExternalLossesInputs.length ? (
          <SectionLoss>
            <Text type="h4">
              {t("summaryPage.widgets.losses.title.external_losses", translations)}
            </Text>
            <StyledTable>
              <Table
                datas={sortedExternalLossesInputs}
                sort={sort}
                onSort={setSort}
                metadata={{ ...externalInputMetadata, ...dynamicMetadata }}
                computeRowKey={(losses: ExternalLosses) => String(losses.order)}
              />
            </StyledTable>
          </SectionLoss>
        ) : null}
      </LoadingBoundary>
    </WidgetCard>
  );
}

export default React.memo(Losses);
